import {
  AlignCenter,
  AlignLeft,
  AlignRight,
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  Bold,
  Copy,
  Italic,
  Trash2,
  Underline,
} from 'lucide-react'
import type { MouseEvent, ReactNode } from 'react'

import {
  LABEL_FIELD_SOURCES,
  type LabelFieldSourceType,
} from '@aeris/contracts'
import {
  sheetGeometry,
  spreadRowGapMm,
  type LabelMeasures,
} from '@aeris/printing'
import { Button, CheckboxField, Field, Select, Textarea } from '@aeris/ui'

import {
  FIELD_LABELS,
  describeElement,
  type BarcodeElement,
  type DraftElement,
  type TextElement,
} from '../../lib/label-template-draft.ts'

import {
  ColorField,
  EditorSection,
  NumberField,
  Segmented,
  ToggleButton,
} from './controls.tsx'

/**
 * O painel de propriedades do editor de etiqueta (F129).
 *
 * Nada selecionado: o papel e a etiqueta. Um campo selecionado: só as
 * propriedades DELE, agrupadas como numa ferramenta de desenho — conteúdo,
 * posição, texto, cores. É o que a tabela de dezessete colunas tentava ser.
 */

const FONTS = [
  'Arial',
  'Arial Black',
  'Tahoma',
  'Verdana',
  'Courier New',
  'Times New Roman',
] as const

/** Rotação em décimos de grau, como o `.conf` do GDOOR grava. */
const ROTATIONS = [
  { value: 0, label: '0°', title: 'Sem rotação' },
  { value: 900, label: '90°', title: 'Girar 90°' },
  { value: 1800, label: '180°', title: 'Girar 180°' },
  { value: 2700, label: '270°', title: 'Girar 270°' },
] as const

const ALIGNMENTS = [
  {
    value: 'left',
    label: <AlignLeft aria-hidden="true" className="h-4 w-4" />,
    title: 'Alinhar à esquerda',
  },
  {
    value: 'center',
    label: <AlignCenter aria-hidden="true" className="h-4 w-4" />,
    title: 'Centralizar',
  },
  {
    value: 'right',
    label: <AlignRight aria-hidden="true" className="h-4 w-4" />,
    title: 'Alinhar à direita',
  },
] as const

type Box = Pick<TextElement, 'leftMm' | 'topMm' | 'widthMm' | 'heightMm'>

export const SheetInspector = ({
  measures,
  onChange,
}: {
  measures: LabelMeasures
  onChange: (patch: Partial<LabelMeasures>) => void
}): ReactNode => {
  const geometry = sheetGeometry(measures)
  const isRoll = measures.paper === 'roll'

  const measure = (
    key: keyof Omit<LabelMeasures, 'paper'>,
    label: string,
    options: { integer?: boolean; min?: number; unit?: string } = {}
  ): ReactNode => (
    <NumberField
      label={label}
      value={measures[key]}
      integer={options.integer ?? false}
      min={options.min ?? 0}
      unit={options.unit ?? 'mm'}
      onChange={(value) => {
        onChange({ [key]: value })
      }}
    />
  )

  return (
    <>
      <div className="border-b border-(--border-subtle) px-4 py-3">
        <h2 className="text-(length:--text-sm) font-(--weight-semibold) text-(--text-primary)">
          Papel e etiqueta
        </h2>
        <p className="text-(length:--text-xs) text-(--text-muted)">
          Nenhum campo selecionado. Clique num campo na prévia ou na lista para
          editá-lo.
        </p>
      </div>

      <EditorSection title="Papel">
        <Segmented
          label="Tipo do papel"
          value={measures.paper}
          options={[
            { value: 'roll', label: 'Bobina', title: 'Bobina' },
            { value: 'sheet', label: 'Folha', title: 'Folha' },
          ]}
          onChange={(paper) => {
            onChange({ paper })
          }}
        />
        <div className="grid grid-cols-2 gap-2">
          {measure(
            'pageWidthMm',
            isRoll ? 'Largura da bobina' : 'Largura da folha',
            { min: 1 }
          )}
          {isRoll
            ? null
            : measure('pageHeightMm', 'Altura da folha', { min: 1 })}
        </div>
      </EditorSection>

      <EditorSection title="Etiqueta">
        <div className="grid grid-cols-2 gap-2">
          {measure('labelWidthMm', 'Largura', { min: 1 })}
          {measure('labelHeightMm', 'Altura', { min: 1 })}
          {measure('columns', 'Por linha', { integer: true, min: 1, unit: '' })}
          {isRoll
            ? null
            : measure('rows', 'Linhas por folha', {
                integer: true,
                min: 1,
                unit: '',
              })}
          {measure('columnGapMm', 'Espaço entre colunas')}
          {isRoll ? null : measure('rowGapMm', 'Espaço entre fileiras')}
        </div>
        {isRoll ? null : (
          /*
           * F140: como o GDOOR distribui a folha — a altura útil dividida pelas
           * fileiras. É o ponto de partida; a folha de teste diz se falta
           * ajustar um décimo.
           */
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => {
              onChange({ rowGapMm: spreadRowGapMm(measures) })
            }}
          >
            Espalhar as fileiras pela folha
          </Button>
        )}
        <p
          aria-live="polite"
          className="rounded-(--radius-md) bg-(--surface-sunken) px-3 py-2 text-(length:--text-xs) text-(--text-secondary)"
        >
          {isRoll
            ? `Cabem ${String(geometry.columns)} por linha na bobina.`
            : `Cabem ${String(geometry.columns)} × ${String(geometry.rows)} = ${String(geometry.columns * geometry.rows)} etiquetas por folha.`}
          {geometry.columns < measures.columns
            ? ' A largura não comporta todas as que foram pedidas por linha.'
            : ''}
        </p>
      </EditorSection>

      <EditorSection title="Margens">
        <div className="grid grid-cols-2 gap-2">
          {measure('marginTopMm', 'Superior')}
          {isRoll ? null : measure('marginBottomMm', 'Inferior')}
          {measure('marginLeftMm', 'Esquerda')}
          {measure('marginRightMm', 'Direita')}
        </div>
      </EditorSection>
    </>
  )
}

const PositionSection = ({
  box,
  onChange,
  onNudge,
}: {
  box: Box
  onChange: (patch: Partial<Box>) => void
  onNudge: (dxMm: number, dyMm: number) => void
}): ReactNode => {
  const step = (event: MouseEvent): number => (event.shiftKey ? 5 : 0.5)
  const arrow = (
    label: string,
    icon: ReactNode,
    move: (distance: number) => [number, number]
  ): ReactNode => (
    <Button
      variant="secondary"
      size="sm"
      aria-label={label}
      title={`${label} (Shift: 5 mm)`}
      leadingIcon={icon}
      onClick={(event) => {
        const [dx, dy] = move(step(event))
        onNudge(dx, dy)
      }}
    />
  )

  return (
    <EditorSection title="Posição e tamanho">
      <div className="grid grid-cols-2 gap-2">
        <NumberField
          label="Esquerda"
          unit="mm"
          min={-1000}
          value={box.leftMm}
          onChange={(leftMm) => {
            onChange({ leftMm })
          }}
        />
        <NumberField
          label="Topo"
          unit="mm"
          min={-1000}
          value={box.topMm}
          onChange={(topMm) => {
            onChange({ topMm })
          }}
        />
        <NumberField
          label="Largura"
          unit="mm"
          value={box.widthMm}
          onChange={(widthMm) => {
            onChange({ widthMm })
          }}
        />
        <NumberField
          label="Altura"
          unit="mm"
          value={box.heightMm}
          onChange={(heightMm) => {
            onChange({ heightMm })
          }}
        />
      </div>
      <div className="flex items-center gap-3">
        <div className="grid w-fit grid-cols-3 gap-1">
          <span />
          {arrow(
            'Mover para cima',
            <ArrowUp aria-hidden="true" className="h-4 w-4" />,
            (d) => [0, -d]
          )}
          <span />
          {arrow(
            'Mover para a esquerda',
            <ArrowLeft aria-hidden="true" className="h-4 w-4" />,
            (d) => [-d, 0]
          )}
          <span />
          {arrow(
            'Mover para a direita',
            <ArrowRight aria-hidden="true" className="h-4 w-4" />,
            (d) => [d, 0]
          )}
          <span />
          {arrow(
            'Mover para baixo',
            <ArrowDown aria-hidden="true" className="h-4 w-4" />,
            (d) => [0, d]
          )}
          <span />
        </div>
        <p className="text-(length:--text-xs) text-(--text-muted)">
          Cada clique move 0,5 mm; com Shift, 5 mm. As setas do teclado fazem o
          mesmo.
        </p>
      </div>
    </EditorSection>
  )
}

const TextInspector = ({
  element,
  onChange,
  onNudge,
}: {
  element: TextElement
  onChange: (patch: Partial<Omit<TextElement, 'kind'>>) => void
  onNudge: (dxMm: number, dyMm: number) => void
}): ReactNode => {
  const fonts: readonly string[] = (FONTS as readonly string[]).includes(
    element.fontFamily
  )
    ? FONTS
    : [element.fontFamily, ...FONTS]

  return (
    <>
      <EditorSection title="Conteúdo">
        <Field label="O que imprime">
          {(field) => (
            <Select
              {...field}
              className="h-8"
              value={element.source}
              onChange={(event) => {
                const source = event.target.value as LabelFieldSourceType
                onChange({
                  source,
                  ...(source === 'literal' && element.text === ''
                    ? { text: 'Texto' }
                    : {}),
                })
              }}
            >
              {LABEL_FIELD_SOURCES.map((source) => (
                <option key={source} value={source}>
                  {FIELD_LABELS[source]}
                </option>
              ))}
            </Select>
          )}
        </Field>
        {element.source === 'literal' ? (
          <Field label="Texto" hint="Enter quebra a linha.">
            {(field) => (
              <Textarea
                {...field}
                rows={2}
                value={element.text}
                onChange={(event) => {
                  onChange({ text: event.target.value })
                }}
              />
            )}
          </Field>
        ) : null}
        {element.source === 'installment' ? (
          <NumberField
            label="Em quantas vezes"
            unit="X"
            integer
            min={1}
            max={36}
            value={element.installments ?? 10}
            onChange={(installments) => {
              onChange({ installments })
            }}
          />
        ) : null}
      </EditorSection>

      <PositionSection box={element} onChange={onChange} onNudge={onNudge} />

      <EditorSection title="Texto">
        <div className="grid grid-cols-[minmax(0,1fr)_5.5rem] items-end gap-2">
          <Field label="Fonte">
            {(field) => (
              <Select
                {...field}
                className="h-8"
                value={element.fontFamily}
                onChange={(event) => {
                  onChange({ fontFamily: event.target.value })
                }}
              >
                {fonts.map((font) => (
                  <option key={font} value={font}>
                    {font}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <NumberField
            label="Tamanho"
            unit="pt"
            min={1}
            max={400}
            value={element.fontSizePt}
            onChange={(fontSizePt) => {
              onChange({ fontSizePt })
            }}
          />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <ToggleButton
            label="Negrito"
            pressed={element.bold}
            onChange={(bold) => {
              onChange({ bold })
            }}
          >
            <Bold aria-hidden="true" className="h-4 w-4" />
          </ToggleButton>
          <ToggleButton
            label="Itálico"
            pressed={element.italic}
            onChange={(italic) => {
              onChange({ italic })
            }}
          >
            <Italic aria-hidden="true" className="h-4 w-4" />
          </ToggleButton>
          <ToggleButton
            label="Sublinhado"
            pressed={element.underline}
            onChange={(underline) => {
              onChange({ underline })
            }}
          >
            <Underline aria-hidden="true" className="h-4 w-4" />
          </ToggleButton>
          <Segmented
            label="Alinhamento"
            value={element.align}
            options={ALIGNMENTS}
            onChange={(align) => {
              onChange({ align })
            }}
          />
        </div>
        <CheckboxField
          label="Quebrar em várias linhas"
          checked={element.wrap}
          onCheckedChange={(state) => {
            onChange({ wrap: state === true })
          }}
        />
        <div className="flex flex-col gap-1">
          <span className="text-(length:--text-xs) text-(--text-secondary)">
            Rotação
          </span>
          <Segmented
            label="Rotação"
            value={element.rotationDeg}
            options={ROTATIONS}
            onChange={(rotationDeg) => {
              onChange({ rotationDeg })
            }}
          />
        </div>
      </EditorSection>

      <EditorSection title="Cores">
        <ColorField
          label="Cor do texto"
          value={element.color}
          onChange={(color) => {
            onChange({ color })
          }}
        />
        <CheckboxField
          label="Fundo colorido (tarja)"
          checked={element.background !== null}
          onCheckedChange={(state) => {
            onChange({ background: state === true ? '#000000' : null })
          }}
        />
        {element.background === null ? null : (
          <ColorField
            label="Cor do fundo"
            value={element.background}
            onChange={(background) => {
              onChange({ background })
            }}
          />
        )}
      </EditorSection>
    </>
  )
}

const BarcodeInspector = ({
  element,
  onChange,
  onNudge,
}: {
  element: BarcodeElement
  onChange: (patch: Partial<Omit<BarcodeElement, 'kind'>>) => void
  onNudge: (dxMm: number, dyMm: number) => void
}): ReactNode => (
  <>
    <PositionSection box={element} onChange={onChange} onNudge={onNudge} />
    <EditorSection title="Barras">
      <NumberField
        label="Largura da barra mais fina"
        min={0.1}
        max={20}
        value={element.moduleWidth}
        onChange={(moduleWidth) => {
          onChange({ moduleWidth })
        }}
      />
      <CheckboxField
        label="Mostrar o número embaixo"
        checked={element.showText}
        onCheckedChange={(state) => {
          onChange({ showText: state === true })
        }}
      />
      <ColorField
        label="Cor das barras"
        value={element.color}
        onChange={(color) => {
          onChange({ color })
        }}
      />
      <p className="text-(length:--text-xs) text-(--text-muted)">
        Produto com EAN sai em EAN-13; sem EAN, em CODE128 do código interno — o
        que o leitor do PDV procura.
      </p>
    </EditorSection>
  </>
)

export const ElementInspector = ({
  element,
  onText,
  onBarcode,
  onNudge,
  onDuplicate,
  onRemove,
}: {
  element: DraftElement
  onText: (patch: Partial<Omit<TextElement, 'kind'>>) => void
  onBarcode: (patch: Partial<Omit<BarcodeElement, 'kind'>>) => void
  onNudge: (dxMm: number, dyMm: number) => void
  onDuplicate: () => void
  onRemove: () => void
}): ReactNode => (
  <>
    <div className="flex items-center gap-1 border-b border-(--border-subtle) px-4 py-3">
      <div className="min-w-0 flex-1">
        <p className="text-(length:--text-xs) text-(--text-muted)">
          Campo selecionado
        </p>
        <h2 className="truncate text-(length:--text-sm) font-(--weight-semibold) text-(--text-primary)">
          {describeElement(element)}
        </h2>
      </div>
      <Button
        variant="ghost"
        size="sm"
        aria-label="Duplicar campo"
        leadingIcon={<Copy aria-hidden="true" className="h-4 w-4" />}
        onClick={onDuplicate}
      />
      <Button
        variant="ghost"
        size="sm"
        aria-label="Excluir campo"
        leadingIcon={
          <Trash2 aria-hidden="true" className="h-4 w-4 text-(--danger)" />
        }
        onClick={onRemove}
      />
    </div>
    {element.kind === 'text' ? (
      <TextInspector element={element} onChange={onText} onNudge={onNudge} />
    ) : (
      <BarcodeInspector
        element={element}
        onChange={onBarcode}
        onNudge={onNudge}
      />
    )}
  </>
)
